import React, {
  createContext,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  startTransition,
} from "react";
import { flushSync } from "react-dom";
import { Drawer, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import { useWelcomeReviewsDrawerCloseEffect } from "pages/Welcome/useWelcomeReviewsDrawerCloseEffect";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";

/** Imperative API so opening reviews does not re-render WelcomePage / GroupWidgets. */
export const WelcomeReviewsDrawerRefContext = createContext(null);

const WelcomeReviewsDrawerHost = forwardRef(function WelcomeReviewsDrawerHost(
  _props,
  ref,
) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueReviews, setIssueReviews] = useState([]);
  const [showReviewList, setShowReviewList] = useState(false);

  const handleDrawerClose = useCallback(() => {
    flushSync(() => {
      setDrawerOpen(false);
      setReviewsLoading(false);
      setSelectedIssue(null);
      setIssueReviews([]);
      setShowReviewList(false);
    });
  }, []);

  useWelcomeReviewsDrawerCloseEffect(drawerOpen, handleDrawerClose);

  useImperativeHandle(
    ref,
    () => ({
      openLoading(issue) {
        setIssueReviews([]);
        setShowReviewList(false);
        setReviewsLoading(true);
        setSelectedIssue(issue || null);
        setDrawerOpen(true);
      },
      setReviews(reviews, meta) {
        const list = Array.isArray(reviews) ? reviews : [];
        setIssueReviews(list);
        setReviewsLoading(false);
        if (meta?.countLabel) {
          setSelectedIssue((prev) =>
            prev ? { ...prev, root_cause: String(meta.countLabel) } : prev,
          );
        }
        if (list.length === 0) {
          setShowReviewList(true);
          return;
        }
        setShowReviewList(false);
        requestAnimationFrame(() => {
          startTransition(() => {
            setShowReviewList(true);
          });
        });
      },
      setLoading(loading) {
        setReviewsLoading(Boolean(loading));
        if (loading) {
          setShowReviewList(false);
        }
      },
      close: handleDrawerClose,
    }),
    [handleDrawerClose],
  );

  const drawerTitle = selectedIssue?.label
    ? String(selectedIssue.label)
    : t("common.reviews", "Reviews");

  const drawerSubtitle = selectedIssue?.root_cause
    ? String(selectedIssue.root_cause)
    : null;

  return (
    <Drawer
      {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
      title={drawerTitle}
      extra={
        <button
          type="button"
          className="welcome-reviews-drawer-header-close"
          onClick={handleDrawerClose}
        >
          {t("common.close", "Close")}
        </button>
      }
      onClose={handleDrawerClose}
      open={drawerOpen}
      width={welcomeReviewsDrawerWidth()}
    >
      {drawerSubtitle ? (
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 0, marginBottom: 16, wordBreak: "break-word" }}
        >
          {drawerSubtitle}
        </Typography.Paragraph>
      ) : null}
      <Spin spinning={reviewsLoading}>
        {!reviewsLoading || showReviewList ? (
          <WelcomeDrawerReviewsList
            allReviews={issueReviews}
            reviewsLoading={reviewsLoading}
            showTotalCount
            exportFileLabel={drawerTitle}
          />
        ) : null}
      </Spin>
    </Drawer>
  );
});

export default WelcomeReviewsDrawerHost;
